import { useState, useEffect, useMemo } from 'react';
import { Header } from '../../common/Header/Header.jsx';
import {
  IntermediaryComissionSummaryTable,
  IntermediaryComissionDetailTable,
} from '../../common/Tables/Tables';
import {
  intermediaryComissionSummaryHeader,
  intermediaryComissionDetailHeader,
} from '../../../assets/helpers/tables';
import { notify } from '../../../assets/helpers/toast';
import { intermediaryComissionService } from '../../../services/intermediaryComissionService';
import moment from 'moment';
import { filterParams } from '../../../assets/helpers/funciones';
import { LoadSpinner } from '../../common/LoadSpinner/LoadSpinner';
import {
  FilterDatePicker,
  Select,
} from '../../common/Forms/Input/Input';
import { ActionButton } from '../../common/Buttons/Buttons';
import authService from '../../../services/authService';
import '../Comissions/Comissions.scss';
import './IntermediaryComissions.scss';

const parseNum = (value) => {
  const num = parseFloat(value);
  return Number.isNaN(num) ? 0 : num;
};

const getDefaultEndDate = () => moment().startOf('day').toDate();

const getDefaultStartDate = () =>
  moment().subtract(7, 'days').startOf('day').toDate();

export const IntermediaryComissions = () => {
  // An agency master only sees its agency (the backend filters by token) and
  // can't recalculate commissions
  const isAgencyMaster = authService?.isAgencyMaster();
  const [startDate, setStartDate] = useState(getDefaultStartDate);
  const [endDate, setEndDate] = useState(getDefaultEndDate);
  const [isLoading, setIsloading] = useState(false);
  const [summaryData, setSummaryData] = useState([]);
  const [detailData, setDetailData] = useState([]);
  const [detailTotal, setDetailTotal] = useState(0);
  const [intermediaryFilter, setIntermediaryFilter] = useState('');
  const [intermediaryOptions, setIntermediaryOptions] = useState([
    { name: 'Todos', _id: '' },
  ]);
  const [pdfLoadingId, setPdfLoadingId] = useState('');

  const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
  const formattedEndDate = moment(endDate).format('YYYY-MM-DD');

  const buildIntermediaryOptions = (comissions) => [
    { name: 'Todos', _id: '' },
    ...comissions.map((item) => ({
      name: item?.sellerFullName,
      _id: item?.sellerId,
    })),
  ];

  const loadDetail = async (intermediaryId) => {
    if (!startDate || !endDate) {
      return null;
    }

    const params = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      page: 1,
      limit: 100,
    };

    if (intermediaryId) {
      params.intermediary = intermediaryId;
    }

    try {
      const response = await intermediaryComissionService.getDetail(
        filterParams(params)
      );

      if (![200, 201].includes(response?.status)) {
        notify(`${response?.message}` || 'error', 'error');
        setDetailData([]);
        setDetailTotal(0);
        return;
      }

      setDetailData(response?.data?.data || []);
      setDetailTotal(response?.data?.total ?? 0);
    } catch (error) {
      console.log('error', error);
      setDetailData([]);
      setDetailTotal(0);
    }
  };

  const loadCommissions = async () => {
    if (!startDate || !endDate) {
      return null;
    }

    const params = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      page: 1,
      limit: 500,
    };

    setIsloading(true);
    setSummaryData([]);
    setDetailData([]);
    setDetailTotal(0);

    try {
      const response = await intermediaryComissionService.getList(
        filterParams(params)
      );

      if (![200, 201].includes(response?.status)) {
        notify(`${response?.message}` || 'error', 'error');
        return;
      }

      const comissions = response?.data?.comissions || [];
      setSummaryData(comissions);
      setIntermediaryOptions(buildIntermediaryOptions(comissions));
      setIntermediaryFilter('');

      await loadDetail('');
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsloading(false);
    }
  };

  const handleIntermediaryFilter = async (sellerId) => {
    setIntermediaryFilter(sellerId);
    setIsloading(true);
    await loadDetail(sellerId);
    setIsloading(false);
  };

  const handleRecalculate = async () => {
    if (!startDate || !endDate || isLoading) {
      return;
    }

    setIsloading(true);
    try {
      const response = await intermediaryComissionService.calculate({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });

      if ([200, 201].includes(response?.status)) {
        const { sellers, memberships } = response?.data || {};
        notify(
          `Comisiones calculadas: ${sellers ?? 0} intermediarios, ${memberships ?? 0} membresías`,
          'success'
        );
        await loadCommissions();
        return;
      }

      notify(`${response?.message}` || 'error', 'error');
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsloading(false);
    }
  };

  const handleDownloadZip = async () => {
    if (!startDate || !endDate || isLoading) {
      return;
    }

    setIsloading(true);
    try {
      const response = await intermediaryComissionService.downloadZip({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });

      if (![200, 201].includes(response?.status)) {
        notify(`${response?.message}` || 'error', 'error');
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsloading(false);
    }
  };

  const handleDownloadPdf = async (intermediaryId) => {
    if (!startDate || !endDate || !intermediaryId || pdfLoadingId) {
      return;
    }

    setPdfLoadingId(intermediaryId);
    try {
      const response = await intermediaryComissionService.downloadPdf({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        intermediary: intermediaryId,
      });

      if (![200, 201].includes(response?.status)) {
        notify(`${response?.message}` || 'error', 'error');
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setPdfLoadingId('');
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      loadCommissions();
    }
  }, [startDate, endDate]);

  const summaryTotals = useMemo(() => {
    return summaryData.reduce(
      (acc, row) => {
        acc.totalSales += parseNum(row?.sold?.total ?? row?.sold?.plan);
        acc.totalPlan += parseNum(row?.amount?.plan);
        acc.totalRcv += parseNum(row?.amount?.rcv);
        acc.totalNet += parseNum(row?.amount?.total);
        acc.comissionBs += parseNum(row?.comm?.bsInUsd);
        acc.comissionUsd += parseNum(row?.comm?.usd ?? row?.comm?.total);
        return acc;
      },
      {
        totalSales: 0,
        totalPlan: 0,
        totalRcv: 0,
        totalNet: 0,
        comissionBs: 0,
        comissionUsd: 0,
      }
    );
  }, [summaryData]);

  const detailTotals = useMemo(() => {
    return detailData.reduce(
      (acc, row) => {
        acc.totalNet += parseNum(row?.price?.total);
        acc.comission += parseNum(row?.comission);
        return acc;
      },
      { totalNet: 0, comission: 0 }
    );
  }, [detailData]);

  return (
    <>
      <Header />
      <LoadSpinner show={isLoading} />
      <div className="service-with-container container">
        <div className="buttons-container">
          <h1 className="title-component">
            {isAgencyMaster
              ? 'Comisiones de mi agencia'
              : 'Comisiones Intermediarios'}
          </h1>
        </div>

        <div className="intermediary-comission-filters">
          <FilterDatePicker
            bootstrap={''}
            label={'Fecha inicio'}
            name={'startDate'}
            placeholder={''}
            value={startDate}
            onChange={(date) => date && setStartDate(date)}
          />
          <FilterDatePicker
            bootstrap={''}
            label={'Fecha fin'}
            name={'endDate'}
            placeholder={''}
            value={endDate}
            onChange={(date) => date && setEndDate(date)}
          />
          <div style={{ display: 'flex', marginBottom: '5px' }}>
            {!isAgencyMaster && (
              <ActionButton
                label={'Recalcular'}
                disabled={isLoading}
                action={handleRecalculate}
              />
            )}
            <ActionButton
              label={'Descargar Zip'}
              disabled={isLoading}
              action={handleDownloadZip}
            />
          </div>
        </div>

        <IntermediaryComissionSummaryTable
          header={intermediaryComissionSummaryHeader}
          data={summaryData}
          totals={summaryTotals}
          onDownloadPdf={handleDownloadPdf}
          pdfLoadingId={pdfLoadingId}
        />

        <div
          className="intermediary-comission-filters"
          style={{ marginTop: '8px' }}
        >
          <div className="col-12 col-md-3">
            <Select
              options={intermediaryOptions}
              label={isAgencyMaster ? 'Asesor' : 'Intermediario'}
              value={intermediaryFilter}
              capitalizeOff={false}
              onChange={(e) => handleIntermediaryFilter(e?.target?.value)}
            />
          </div>
        </div>

        <IntermediaryComissionDetailTable
          header={intermediaryComissionDetailHeader}
          data={detailData}
          totals={detailTotals}
          totalCount={detailTotal}
        />

        {summaryData?.length === 0 && !isLoading && (
          <h4 style={{ textAlign: 'center', marginTop: '24px' }}>
            No hay data disponible para el periodo seleccionado
          </h4>
        )}
      </div>
    </>
  );
};
